/**
 * ResearchComposer - the research input.
 *
 * Purpose-built rather than the generic chat PromptBar: it carries the intent
 * chip, attachment pills, and the entries to the source picker and session
 * settings, and it sends `{intent, attachmentUuids}` with the query.
 */

import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';
import { PaperPlaneTiltIcon } from './icons';
import { PiPaperclipLight, PiSlidersHorizontalLight,
  PiBookmarkSimpleLight, PiStopCircleLight,
  PiPencilSimpleLight,
} from 'react-icons/pi';
import IntentSelector, { ComposerIntent } from '../IntentSelector';
import AttachmentPills from '../AttachmentPills';
import { ComposerAttachment, hasPendingUploads } from '../attachments';
import { useAppDispatch } from '../../../store';
import { showModal } from '../../../store/actions/modalActions';

interface Props {
  onSend: (text: string) => void;
  onStop: () => void;
  isRunning: boolean;
  disabled?: boolean;

  /**
   * The answer-shape chip.
   *
   * OPTIONAL, both of them: a surface that pins the answer shape (the matter
   * Assistant, which only ever answers) omits `onIntentChange` and the chip is
   * not rendered at all, rather than offered and then ignored. Same contract as
   * `onOpenSettings` below.
   */
  intent?: ComposerIntent;
  onIntentChange?: (intent: ComposerIntent) => void;

  attachments: ComposerAttachment[];
  onAttachFiles: (files: File[]) => void;
  onRemoveAttachment: (id: string) => void;

  /**
   * NARROW MODE - the composer inside a 360–900px pane.
   *
   * The full-page composer centres a `max-w-3xl` reading column inside a wide
   * canvas; in a pane that column IS the pane, so the centring is dead weight
   * and the page-scale padding eats the width the textarea needs. Compact
   * drops both and shortens the auto-grow ceiling, so the box cannot swallow
   * the transcript above it. Nothing else about the composer changes.
   */
  compact?: boolean;
  /** Overrides the default research placeholder. */
  placeholder?: string;
  /**
   * Opens the source picker / session settings panel.
   *
   * OPTIONAL: a surface with no settings panel (the matter Assistant pane)
   * omits it and the entry is not rendered, rather than being shown inert.
   */
  onOpenSettings?: () => void;
  /** Count shown on the settings entry. */
  sourceCount?: number;
  /**
   * Opens the prompt library with the box's current text (to save it).
   * OPTIONAL, like onOpenSettings: no library, no entry.
   */
  onOpenPrompts?: (draft: string) => void;
  /** The chat's document set, rendered beside the intent and source controls. */
  documents?: React.ReactNode;
  /**
   * Revising an existing question rather than asking a new one.
   *
   * `seed` is pushed into the box whenever `token` changes - a token rather
   * than the text itself, so re-editing the SAME question after typing over
   * it still refills the box.
   */
  edit?: { messageUuid: string; seed: string; token: number } | null;
  onCancelEdit?: () => void;
  /** Push text back into the box (a stashed draft, or '' to clear it). */
  restore?: { text: string; token: number } | null;
  /** Mirrors the box's text out, so a draft can be stashed before an edit. */
  onDraftChange?: (text: string) => void;
}

const ResearchComposer: React.FC<Props> = ({
  onSend,
  onStop,
  isRunning,
  disabled = false,
  intent = null,
  onIntentChange,
  attachments,
  onAttachFiles,
  onRemoveAttachment,
  compact = false,
  placeholder,
  onOpenSettings,
  sourceCount = 0,
  onOpenPrompts,
  documents,
  edit,
  onCancelEdit,
  restore,
  onDraftChange,
}) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const [input, setInput] = useState('');

  /**
   * Load the question being edited, and put the cursor at the END of it —
   * the user is revising, not retyping, so selecting the whole thing (or
   * landing at the start) would be in the way.
   */
  useEffect(() => {
    if (!edit) return;
    setInput(edit.seed);
    const node = textareaRef.current;
    if (!node) return;
    node.focus();
    const end = edit.seed.length;
    node.setSelectionRange(end, end);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [edit?.token]);
  useEffect(() => {
    if (!restore) return;
    setInput(restore.text);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [restore?.token]);

  useEffect(() => { onDraftChange?.(input); }, [input, onDraftChange]);

  const [isDragging, setIsDragging] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);

  const uploadsPending = hasPendingUploads(attachments);
  const canSend = input.trim().length > 0 && !uploadsPending && !disabled && !isRunning;

  useEffect(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, compact ? 120 : 200)}px`;
  }, [input, compact]);

  const submit = () => {
    if (!canSend) return;
    // In edit mode the surface routes this to the message's rerun and owns
    // clearing the box (it may have a stashed draft to put back), so the
    // composer does not clear it here.
    onSend(input.trim());
    if (!edit) setInput('');
  };

  /**
   * Escape leaves edit mode - but only when nothing has been changed.
   * Discarding a rewrite on a stray keypress is the surprise worth avoiding;
   * the Cancel button is always there for a deliberate exit.
   */
  const escapeCancels = !!edit && input === edit.seed;

  const accept = (files: File[]) => {
    if (files.length) onAttachFiles(files);
  };

  /**
   * The paperclip opens the shared global file-picker modal (#27) instead of a
   * local hidden <input>. The modal renders the shared FileUploader - keeping
   * its extension whitelist, size cap and magic-byte validation - and hands the
   * picked File[] back through onConfirm, which feeds the SAME research
   * attachment pipeline (onAttachFiles) that drag-and-drop uses.
   */
  const openAttachModal = () => {
    if (disabled) return;
    dispatch(
      showModal({
        type: 'fileUploader',
        showModal: true,
        message: '',
        title: t('Attach files'),
        icon: <PiPaperclipLight className="w-6 h-6 text-gray-600" />,
        confirmLabel: t('Attach'),
        onConfirm: (files: File[]) => accept(files),
      })
    );
  };

  return (
    <div
      className={clsx('flex-shrink-0', compact ? 'px-2 pb-2' : 'px-4 pb-3')}
      data-testid="research-composer"
      data-compact={compact}
      onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
      onDragLeave={(e) => { e.preventDefault(); setIsDragging(false); }}
      onDrop={(e) => {
        e.preventDefault();
        setIsDragging(false);
        accept(Array.from(e.dataTransfer.files));
      }}
    >
      <div className={compact ? '' : 'max-w-3xl mx-auto'}>
        {edit && (
          <div
            className="mb-2 flex items-center justify-between gap-2 px-2.5 py-1.5
                       rounded-lg text-[11px] bg-champagne/10 border border-champagne/40"
            data-testid="composer-editing"
          >
            <span className="inline-flex items-center gap-1.5 text-fg-strong min-w-0">
              <PiPencilSimpleLight className="w-3.5 h-3.5 flex-shrink-0" aria-hidden />
              {/* Named plainly: this send REPLACES an answer, it does not ask
                  a new question, and nothing else on screen says so. */}
              <span className="truncate">{t('Editing your question')}</span>
            </span>
            <button
              type="button"
              onClick={onCancelEdit}
              data-testid="composer-cancel-edit"
              className="flex-shrink-0 px-2 py-0.5 rounded-full text-fg-muted
                         hover:text-fg-strong transition-colors duration-200 ease-slide"
            >
              {t('Cancel')}
            </button>
          </div>
        )}

        <AttachmentPills attachments={attachments} onRemove={onRemoveAttachment} />

        <div
          className={clsx(
            'relative flex items-end gap-2 rounded-xl px-3 py-3 glass',
            'transition-all duration-300 ease-slide',
            isDragging && 'ring-2 ring-dashed ring-champagne/60'
          )}
        >
          <textarea
            ref={textareaRef}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder={placeholder ?? t('Ask a legal research question…') ?? ''}
            rows={1}
            disabled={disabled}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                submit();
              }
              if (e.key === 'Escape' && escapeCancels) {
                e.preventDefault();
                onCancelEdit?.();
              }
            }}
            className={clsx(
              'flex-1 bg-transparent text-fg-body placeholder:text-fg-muted',
              'resize-none focus:outline-none border-0 text-sm',
              compact ? 'min-h-[2rem]' : 'min-h-[2.5rem]'
            )}
          />

          <div className="flex items-center gap-1 flex-shrink-0">
            <button
              type="button"
              onClick={openAttachModal}
              disabled={disabled}
              aria-label={t('Attach files')}
              title={t('Attach files')}
              className="p-2 rounded-full border border-hairline-soft bg-surface-sunken text-fg-body
                         hover:text-champagne hover:border-champagne/50
                         transition-all duration-200 ease-slide disabled:opacity-50"
            >
              <PiPaperclipLight className="w-4 h-4" />
            </button>

            {isRunning ? (
              <button
                type="button"
                onClick={onStop}
                aria-label={t('Stop')}
                className="p-2 rounded-full text-fg-muted hover:text-danger-500
                           transition-colors duration-200 ease-slide"
              >
                <PiStopCircleLight className="w-5 h-5" />
              </button>
            ) : (
              <button
                type="button"
                onClick={submit}
                disabled={!canSend}
                aria-label={t('Send')}
                title={uploadsPending ? t('Waiting for uploads to finish') : undefined}
                className="p-2 rounded-full text-fg-body hover:text-champagne
                           transition-colors duration-200 ease-slide disabled:opacity-40"
              >
                <PaperPlaneTiltIcon className="w-5 h-5" />
              </button>
            )}
          </div>

          {isDragging && (
            <div className="absolute inset-0 flex items-center justify-center rounded-xl
                            bg-ink/20 backdrop-blur-[2px] z-10">
              <p className="text-sm font-medium text-fg-strong">{t('Drop files here')}</p>
            </div>
          )}
        </div>

        {/* Intent + sources, the two things that change what comes back.
            The whole row goes when neither is offered. */}
        <div
          className={clsx(
            'mt-2 flex items-center gap-2 flex-wrap',
            !onIntentChange && !documents && !onOpenSettings && !onOpenPrompts && 'hidden'
          )}
        >
          {onIntentChange && (
            <IntentSelector value={intent} onChange={onIntentChange} disabled={disabled} />
          )}
          {documents}
          {onOpenSettings && (
            <button
              type="button"
              onClick={onOpenSettings}
              data-testid="composer-settings"
              className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium
                         bg-surface-sunken text-fg-body border border-hairline-soft
                         hover:border-champagne/50 transition-colors duration-200 ease-slide"
            >
              <PiSlidersHorizontalLight className="w-3.5 h-3.5" aria-hidden />
              {sourceCount > 0
                ? `${sourceCount} ${t(sourceCount === 1 ? 'source' : 'sources')}`
                : t('Sources & settings')}
            </button>
          )}
          {onOpenPrompts && (
            <button
              type="button"
              onClick={() => onOpenPrompts(input)}
              data-testid="composer-prompts"
              className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium
                         bg-surface-sunken text-fg-body border border-hairline-soft
                         hover:border-champagne/50 transition-colors duration-200 ease-slide"
            >
              <PiBookmarkSimpleLight className="w-3.5 h-3.5" aria-hidden />
              {t('Prompts')}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default ResearchComposer;
